import {React, observer, observable, computed, IDisposer, action, useEffect, classNames, useAsObservableSource, Fuse} from '@api'
import {Input, Slider, TextField} from '@material-ui/core'
import {ChangeLogFlexLayoutTabStore, ContentTitleBarHierarchyItem, ContentTitleBarItemField, SearchPopover, useChangeLogFlexLayoutTabStore, useVipLocalStore, VipLocalStore, ChangeLogFilterFieldConfig} from '@modules'
import {ICatalogItemChangeEventRaw, IUser, StoresInstance} from '@stores'
import {autorun} from 'mobx'
import * as css from './ChangeLogMenuItemElement.css'

/// region Store

export class ChangeLogMenuItemElementStore extends VipLocalStore {
  constructor(stores: StoresInstance, public rootStore: ChangeLogFlexLayoutTabStore, public props: Omit<MyProps, 'className'>) {
    super(stores)
  }

  registerEffects = () => {
    const _toDispose: Array<IDisposer> = [
      autorun(async () => {
        const {workspace, stores: {tags}, type} = this

        if (type === 'Tag' || type === 'TagList') {
          await tags.loadTagsFromEs({page: 0, tagType: this.field?.def?.tagType, searchText: ''})
          this.fuse = new Fuse(this.tagValues, {keys: ['name']})
        }
        if (type === 'user') {
          await this._loadUsersList()
        }

        if (type === 'Float' || type === 'Double') {
          await this._loadMinMaxValues()
        }
        if (type === 'Enum' || type === 'EnumList') {
          const {workspaces: {workspaceByIndex}, workspace, field} = this,
            ws = _.first(workspaceByIndex[workspace])
          await ws.catalog.c8.getEnumValuesC8(field.def?.enumType)
          this.fuse = new Fuse(this.enumValues, {keys: ['name']})
        }
      }, {name: 'Get data for all async type'})
    ]

    return () => {
      _toDispose.forEach(d => d())
    }
  }

  @observable users: Array<Partial<IUser>> = []

  @action private _loadMinMaxValues = async () => {
    const {es, rootStore: {queryBody}, stores: {_id}, field: {field}} = this

    const {aggregations: {items: {min, max}}} = es.unwrapHitsResult<ICatalogItemChangeEventRaw>(
      await es.search({
        meta: `Change Log - ${_id}- Fetch min max for field ${field}`,
        index: es.indexes.changeLogIndexes,
        body: {
          ...queryBody,
          aggs: {
            items: {
              filter: {
                terms: {
                  '_log.changes.field': [
                    field
                  ]
                }
              },
              aggs: {
                min: {
                  min: {
                    field
                  }
                },
                max: {
                  max: {
                    field
                  }
                }
              }
            }
          },
          from: 0,
          size: 0
        }
      }))

    this.minmax = {min: min.value ?? 0, max: max.value ?? 0}
  }

  @observable minmax: { min: number, max: number } = {min: 0, max: 0}

  @action private _loadUsersList = async () => {
    const {es, rootStore: {queryBody}, stores: {_id, users: {getById}}} = this

    const {aggregations: {user: {buckets}}} = es.unwrapHitsResult<ICatalogItemChangeEventRaw>(
      await es.search({
        meta: `Change Log - ${_id}- Fetch user list`,
        index: es.indexes.changeLogIndexes,
        body: {
          ...queryBody,
          aggs: {
            user: {
              terms: {
                field: '_log.userId'
              }
            }
          },
          from: 0,
          size: 0
        }
      }))
    this.users = buckets.map(({key}) => getById(key))
    this.fuse = new Fuse(this.users, {keys: ['name']})
  }

  @computed get initValue() {
    const {type, props: {selected}, minmax} = this,
      value = selected?.value

    switch (type) {
      case 'String':
        return value ?? ''
      case 'Int':
        return value ?? 0
      case 'Float':
      case 'Double':
        return value ?? [minmax.min, minmax.max]
      case 'Boolean':
      case 'Enum':
      case 'EnumList':
        return value ?? []
    }
  }

  @computed get isHierarchyType(): boolean {
    const {field} = this
    return !!field.c8
  }

  @computed get hierarchyComponent() {
    const {workspaces: {workspaceByIndex}, workspace: ws, field, onChange, selected} = this,
      workspace = workspaceByIndex[ws]

    if (!workspace) return null

    return <ContentTitleBarHierarchyItem field={field}
                                         stores={{...workspace[0].catalog.stores, catalog: workspace[0].catalog} as StoresInstance}
                                         onChange={onChange}
                                         selected={selected}/>
  }

  get selected() {
    return this.props.selected
  }

  @observable value: any = this.initValue

  @observable excludedValue: any

  @action onExclude = (event, values) => {
    const {props: {onExclude}, excludedValue} = this
    if ((excludedValue ?? []).some(val => val.id === values.value.id)) {
      this.excludedValue = excludedValue.filter(val => val.id !== values.value.id)
    } else {
      this.excludedValue = [...(excludedValue ?? []), values.value]
    }

    if (onExclude) onExclude({value: this.excludedValue})
  }

  @action onChange = (event, values?: any) => {
    const {type, props: {onChange}, selected, field} = this

    switch (type) {
      case 'Int':
      case 'String': {
        this.value = event.target.value
        break
      }
      /*case 'Boolean': {
        this.value = event.toLowerCase() === 'yes'
        break
      }*/
      case 'Double':
      case 'Float':
        this.value = values
        break
      case 'Boolean':
      case 'hierarchy':
      case 'Tag':
      case 'TagList':
      case 'user':
      case 'Enum':
      case 'EnumList': {
        const {value} = this
        if ((value ?? []).some(val => val.id === values.value.id)) {
          this.value = value.filter(val => val.id !== values.value.id)
        } else {
          this.value = [...(value ?? []), values.value]
        }
        break
      }
    }
    if (onChange) onChange({value: this.value})
  }

  get workspace() {
    return this.field.workspace
  }

  get field(): ChangeLogFilterFieldConfig {
    return this.props.field
  }

  @observable fuse

  @computed get enumValues() {
    const {field: {def}} = this
    if (def?.type === 'Enum' || def?.type === 'EnumList') {
      const {enumType, enumValues} = def,
        {workspaces: {workspaceByIndex}, workspace} = this,
        {catalog} = _.first(workspaceByIndex[workspace])
      return catalog.c8.enumByName.get(enumType) ?? enumValues ?? []
    }
    return []
  }

  @computed get list() {
    const {enumValues, tagValues, users, fuse} = this
    return enumValues.length ? enumValues : tagValues.length ? tagValues : users.length ? users : []
  }

  @computed get results() {
    const {list, fuse, searchText} = this
    return searchText ? fuse.search(searchText).map(f => f.item) : list
  }

  @computed get tagValues() {
    const {field: {def}} = this
    if (def?.type === 'Tag' || def?.type === 'TagList') {
      const {stores: {tags: {tagsCache}}} = this
      return tagsCache[def?.tagType]?.hits ?? [].map(h => h._source)
    }
    return []
  }

  @computed get type() {
    const {field, isHierarchyType} = this
    return isHierarchyType ? 'hierarchy' : field.def?.type ?? field.type ?? 'String'
  }

  @computed get label() {
    const {catalog, value, field, type, field: {def}} = this
    switch (type) {
      case 'user':
      case 'Tag':
      case 'TagList':
      case 'Enum':
      case 'EnumList':
      case 'Int':
      case 'hierarchy':
      case 'Boolean':
      case 'String': {
        return catalog.labelForField(field) ?? field.label
      }
    }
  }

  @action onChangeRangeNumeric = (e, index) => {
    const {value, onChange} = this,
      first = _.first(value),
      last = _.last(value)
    if (!index) {
      if (e.target.value <= last) {
        onChange(e, [Number(e.target.value), _.last(value)])
      }
    } else {
      if (e.target.value >= first) {
        onChange(e, [_.first(value), Number(e.target.value)])
      }
    }
  }

  @observable searchText: string = null

  @action onSearch = (e) => {
    this.searchText = e.target.value
  }

  @computed get text() {
    if (!this.props.selected) return ''
    const {props: {selected, excluded}, label} = this,
      text = (selected?.value ?? []).length ? `${selected.value.map((v, i) => i < 2 ? v.name : null).filter(v => v).join(', ')}${selected.value?.length > 2 ? '...' : ''}` : excluded?.value?.length === 0 ? `Select ${label}` : '',
      excludedText = (excluded?.value ?? []).length ? `${excluded.value.map((v, i) => i < 2 ? v.name : null).filter(v => v).join(', ')}${excluded.value?.length > 2 ? '...' : ''}` : ''

    return (selected?.value ?? []).length > 2 ? <span>{text}...&nbsp;<span data-type={'excluded'}>{excludedText}</span></span> : <span>{text}&nbsp;<span data-type={'excluded'}>{excludedText}</span></span>
  }

  @computed get valueComponent() {
    const {type, hierarchyComponent, minmax, value, onChange, searchText, onSearch, label, field, onChangeRangeNumeric, props: {selected, excluded}, text, onExclude, strings} = this

    switch (type) {
      case 'hierarchy': {
        return hierarchyComponent
      }
      case 'String': {
        return <TextField value={value} onChange={onChange} placeholder={`Enter ${label}`}/>
      }
      case 'Boolean': {
        const labels = [{id: 'true', name: (field.def?.booleanLabels?.trueLabel ?? strings.yes)}, {id: 'false', name: (field.def?.booleanLabels?.falseLabel ?? strings.no)}]
        return (
          <SearchPopover hideSearch onSearch={() => {}} searchText={''} text={text} selectedEl={(selected?.value ?? []).length > 0 && selected.value.map((value) => {
            const val: any = _.isObject(value) ? value : {id: value, name: value}
            return <ContentTitleBarItemField key={val.id.toString()}
                                             field={{...field, label: val.name, value: {name: val.name, id: val.id}, workspaceLabel: ''}}
                                             isSelected
                                             onSelectMenuItem={onChange}
                                             hasExclude
                                             renderTo={'applied'}
                                             isExcluded={(excluded?.value ?? []).some(e => e.id === val.id)}
                                             onExcludeMenuItem={onExclude}
                                             searchText={''}/>
          })}>
            {labels.map((val) => {
              return <ContentTitleBarItemField key={val.id.toString()}
                                               field={{...field, label: val.name, value: {name: val.name, id: val.id}, workspaceLabel: ''}}
                                               isSelected={(selected?.value ?? []).some(s => s.id === val.id)}
                                               onSelectMenuItem={onChange}
                                               hasExclude
                                               renderTo={'all'}
                                               isExcluded={(excluded?.value ?? []).some(e => e.id === val.id)}
                                               onExcludeMenuItem={onExclude}
                                               searchText={''}/>
            })}
          </SearchPopover>
        )
      }
      case 'Int': {
        return <TextField type={'number'} value={value} onChange={onChange} placeholder={`Enter ${label}`}/>
      }
      case 'Float':
      case 'Double': {
        return <div className={css.slider}>
          <Input value={_.first(value)}
                 margin='dense'
                 onChange={(e) => {onChangeRangeNumeric(e, 0)}}
                 inputProps={{
                   step: 10,
                   min: minmax.min,
                   max: minmax.max,
                   type: 'number',
                   'aria-labelledby': 'range-slider'
                 }}
          /><Slider value={value}
                    color={'secondary'}
                    onChange={onChange}
                    valueLabelDisplay={'off'}
                    aria-labelledby='range-slider'
                    min={minmax.min}
                    max={minmax.max}
        />
          <Input value={_.last(value)}
                 margin='dense'
                 onChange={(e) => {onChangeRangeNumeric(e, 1)}}
                 inputProps={{
                   step: 10,
                   min: minmax.min,
                   max: minmax.max,
                   type: 'number',
                   'aria-labelledby': 'range-slider'
                 }}
          />
        </div>
      }
      case 'user':
      case 'Tag':
      case 'TagList':
      case 'Enum':
      case 'EnumList': {
        const {results, text} = this
        // text = (selected?.value ?? []).length ? `(${selected.value.length}) Selected` : `Select ${label}`
        return <SearchPopover onSearch={onSearch} searchText={searchText} text={text} selectedEl={(selected?.value ?? []).length > 0 && selected.value.map((value) => {
          const val: any = _.isObject(value) ? value : {id: value, name: value}
          return <ContentTitleBarItemField key={val.id}
                                           field={{...field, label: val.name, value: {name: val.name, id: val.id}, workspaceLabel: ''}}
                                           isSelected
                                           onSelectMenuItem={onChange}
                                           hasExclude
                                           renderTo={'applied'}
                                           isExcluded={(excluded?.value ?? []).some(e => e.id === val.id)}
                                           onExcludeMenuItem={onExclude}
                                           searchText={searchText}/>
        })}>
          {results.map((value) => {
            const val: any = _.isObject(value) ? value : {id: value, name: value}
            return <ContentTitleBarItemField key={val.id}
                                             field={{...field, label: val.name, value: {name: val.name, id: val.id}, workspaceLabel: ''}}
                                             isSelected={(selected?.value ?? []).some(s => s.id === val.id)}
                                             onSelectMenuItem={onChange}
                                             hasExclude
                                             renderTo={'all'}
                                             isExcluded={(excluded?.value ?? []).some(e => e.id === val.id)}
                                             onExcludeMenuItem={onExclude}
                                             searchText={searchText}/>
          })}
        </SearchPopover>
      }
    }
  }

}

export const ChangeLogMenuItemElementContext = React.createContext<ChangeLogMenuItemElementStore>(null)
export const useChangeLogMenuItemElementStore = () => React.useContext(ChangeLogMenuItemElementContext)

/// endregion9+

/// region Components

interface MyProps {
  className?: string
  field: ChangeLogFilterFieldConfig
  onChange: Function
  onExclude?: Function
  selected: any
  excluded?: any
}

export const ChangeLogMenuItemElement = observer<MyProps>(function ChangeLogMenuItemSwitch({className, ...props}) {
  const rootStore = useChangeLogFlexLayoutTabStore(),
    obsProps = useAsObservableSource({...props}),
    store = useVipLocalStore((stores) => new ChangeLogMenuItemElementStore(stores, rootStore, obsProps)),
    {valueComponent, field} = store

  useEffect(() => store.registerEffects(), [])

  return (
    <ChangeLogMenuItemElementContext.Provider value={store}>
      <div className={classNames(css.root, className)}>
        {valueComponent}
      </div>
    </ChangeLogMenuItemElementContext.Provider>)
})

/// endregion




// WEBPACK FOOTER //
// ./src/modules/changelog/ChangeLogMenuItemElement.tsx